import type { Citation } from '@/api/runs';
import { legacyToHtml } from './legacy';
import { sanitizeToFragment } from './sanitize';
import { streamingPrefix } from './streamingHtml';

export interface RenderAnswerOptions {
  /** The text is still arriving: hold back an incomplete end. */
  streaming: boolean;
  /** A stored answer from before chat runs (`format: null`). */
  legacy: boolean;
  /** The `[n]` mapping; only these numbers become citation chips. */
  citations: Citation[] | null;
  /** Accessible names. */
  labels: {
    /** The scrollable region around a table. */
    table: string;
    /** A citation chip, e.g. "Source 1: sop-101.pdf". */
    citation: (n: number, name: string) => string;
    /**
     * The Copy table button above each table; without it no button is
     * added (while streaming, a table may still grow).
     */
    copyTable?: string;
  };
  /**
   * A comparison result (spec §4): its table gets a readable minimum width
   * and its type and impact values become tone badges, as in the kit.
   */
  comparison?: boolean;
}

/** The chip class; the answer view handles clicks and hover on it. */
export const CITATION_CLASS = 'qa-cite';
export const TABLE_SCROLL_CLASS = 'qa-table-scroll';
export const TABLE_COPY_CLASS = 'qa-copy-table';
export const COMPARE_TABLE_CLASS = 'qa-table-compare';
export const BADGE_CLASS = 'qa-badge';

/**
 * The comparison vocabulary of chat-runs-api.md §3.5 and its kit tone:
 * types (document changes and guideline checks) and impact.
 */
const BADGE_TONES: Record<string, string> = {
  change: 'accent',
  changed: 'accent',
  変更: 'accent',
  addition: 'success',
  added: 'success',
  追加: 'success',
  deletion: 'danger',
  deleted: 'danger',
  removed: 'danger',
  削除: 'danger',
  omission: 'warning',
  記載漏れ: 'warning',
  inconsistency: 'danger',
  不整合: 'danger',
  high: 'danger',
  高: 'danger',
  medium: 'warning',
  中: 'warning',
  low: 'neutral',
  低: 'neutral',
  'no differences': 'neutral',
  差異なし: 'neutral',
  partial: 'warning',
  一部のみ: 'warning',
  // Never to be read as "no differences" (spec §4).
  unavailable: 'warning',
  比較不可: 'warning',
};

/**
 * The columns that hold the type and the impact, by their headers (the
 * comparison prompt asks for 種別 / 影響度 and Type / Impact).
 */
const BADGE_COLUMN = /種別|種類|影響|type|impact/i;

/** Each cell's first column, counting colspans. */
const columnStarts = (row: Element) => {
  let column = 0;
  return [...row.children].map(cell => {
    const start = column;
    column += Math.max(1, Number(cell.getAttribute('colspan')) || 1);
    return start;
  });
};

/**
 * In the type and impact columns, a cell holding exactly one vocabulary
 * word shows it as a badge; document text elsewhere stays text.
 */
const addBadges = (table: HTMLTableElement) => {
  const document = table.ownerDocument;
  const header = [...table.querySelectorAll('tr')].find(row =>
    row.querySelector('th'),
  );
  if (!header) return;
  const headerStarts = columnStarts(header);
  const columns = new Set(
    [...header.children]
      .map((cell, index) =>
        BADGE_COLUMN.test(cell.textContent ?? '') ? headerStarts[index] : -1,
      )
      .filter(column => column >= 0),
  );
  if (columns.size === 0) return;
  table.querySelectorAll('tr').forEach(row => {
    const starts = columnStarts(row);
    [...row.children].forEach((cell, index) => {
      if (cell.tagName !== 'TD' || !columns.has(starts[index])) return;
      const word = (cell.textContent ?? '').replace(/\s+/g, ' ').trim();
      const tone = BADGE_TONES[word.toLowerCase()];
      if (!tone || cell.querySelector('*')) return;
      const badge = document.createElement('span');
      badge.className = BADGE_CLASS;
      badge.dataset.tone = tone;
      badge.textContent = word;
      cell.replaceChildren(badge);
    });
  });
};

const CITATION = /\[(\d{1,4})\]/g;
const NO_CHIPS_INSIDE = new Set(['A', 'CODE', 'PRE']);

const insideNoChips = (node: Node) => {
  for (let parent = node.parentNode; parent; parent = parent.parentNode) {
    if (NO_CHIPS_INSIDE.has(parent.nodeName)) return true;
  }
  return false;
};

/** Replace `[n]` with a chip button for each n the API mapped. */
const addCitationChips = (
  root: DocumentFragment,
  citations: Map<number, Citation>,
  label: RenderAnswerOptions['labels']['citation'],
) => {
  const document = root.ownerDocument;
  const walker = document.createTreeWalker(root, 4 /* SHOW_TEXT */);
  const texts: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (CITATION.test(node.nodeValue ?? '') && !insideNoChips(node)) {
      texts.push(node as Text);
    }
    CITATION.lastIndex = 0;
  }
  for (const text of texts) {
    const value = text.nodeValue ?? '';
    const parts = document.createDocumentFragment();
    let last = 0;
    let changed = false;
    for (const match of value.matchAll(CITATION)) {
      const n = Number(match[1]);
      const citation = citations.get(n);
      if (!citation) continue;
      changed = true;
      parts.append(value.slice(last, match.index));
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = CITATION_CLASS;
      chip.setAttribute('data-cite', String(n));
      chip.setAttribute('aria-label', label(n, citation.name ?? ''));
      chip.textContent = String(n);
      parts.append(chip);
      last = (match.index ?? 0) + match[0].length;
    }
    if (!changed) continue;
    parts.append(value.slice(last));
    text.replaceWith(parts);
  }
};

/**
 * Wide tables scroll inside their own region (spec §5); each can be copied
 * on its own (spec §7), by its position among the answer's tables.
 */
const wrapTables = (
  root: DocumentFragment,
  label: string,
  copyLabel: string | undefined,
  comparison = false,
) => {
  const document = root.ownerDocument;
  [...root.querySelectorAll('table')].forEach((table, index) => {
    const region = document.createElement('div');
    region.className = comparison
      ? `${TABLE_SCROLL_CLASS} ${COMPARE_TABLE_CLASS}`
      : TABLE_SCROLL_CLASS;
    if (comparison) addBadges(table);
    region.setAttribute('role', 'region');
    region.setAttribute('tabindex', '0');
    region.setAttribute('aria-label', label);
    table.replaceWith(region);
    region.append(table);
    if (!copyLabel) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = TABLE_COPY_CLASS;
    button.setAttribute('data-table', String(index));
    button.textContent = copyLabel;
    region.before(button);
  });
};

/**
 * Answer text → safe HTML for the answer view: legacy text converted,
 * an incomplete streamed end held back, everything sanitized (allowlist),
 * then tables wrapped and mapped citations turned into chips. The chips and
 * wrappers are added after sanitizing and contain only our own markup.
 */
export const renderAnswer = (
  text: string,
  options: RenderAnswerOptions,
): string => {
  let source = options.legacy ? legacyToHtml(text) : text;
  if (options.streaming) source = streamingPrefix(source);
  if (!source.trim()) return '';

  const fragment = sanitizeToFragment(source);
  wrapTables(
    fragment,
    options.labels.table,
    options.labels.copyTable,
    options.comparison,
  );
  if (options.citations?.length) {
    const byNumber = new Map(
      options.citations.map(citation => [Number(citation.n), citation]),
    );
    addCitationChips(fragment, byNumber, options.labels.citation);
  }
  const container = fragment.ownerDocument.createElement('div');
  container.append(fragment);
  return container.innerHTML;
};
